import { CartUpdateEvent } from '@theme/events';
import { collectCartSectionIds } from '@theme/cart-sections';

/**
 * Owns every mutation for a single kit in the cart — the qty stepper, the qty
 * input and the remove button — for one kit-parent row.
 *
 * A kit is a group of line items sharing properties._kit_id. Each child carries
 * an immutable per-kit quantity in properties._kit_unit_qty; the parent row
 * exposes a kit count (line qty / unit qty). Changing the count multiplies
 * every child's quantity by the new count; decrementing below 1 (or hitting the
 * trash button) removes the whole group. Both go through one /cart/update.js
 * call.
 *
 * Replaces the former document-delegated cart-kit-qty.js + cart-kit-remove.js
 * IIFEs. Scoping the handler to the element gives each kit its own in-flight
 * controller, so a click on one kit can no longer abort another kit's pending
 * request (the module-global AbortController the IIFE shared did exactly that).
 *
 * Markup (snippets/cart-products.liquid):
 *   <cart-kit data-kit-id="…" data-kit-count="…">
 *     <div class="cart-items__kit-qty">
 *       <button data-kit-qty-step="-1">…</button>
 *       <input data-kit-qty-input>
 *       <button data-kit-qty-step="1">…</button>
 *     </div>
 *     <button data-kit-remove="…">…</button>
 *   </cart-kit>
 */
class CartKit extends HTMLElement {
  /** @type {AbortController | null} */
  #inFlight = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  get #kitId() {
    return this.dataset.kitId || '';
  }

  #currentCount() {
    const raw = Number(this.dataset.kitCount);
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  }

  /**
   * Run a cart mutation serialized against every other cart handler, falling
   * back to immediate execution if the shared lock has not loaded.
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  #runLocked(task) {
    return window.AdegenCartLock ? window.AdegenCartLock.run(task) : task();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const removeButton = target.closest('[data-kit-remove]');
    if (removeButton && this.contains(removeButton)) {
      event.preventDefault();
      event.stopPropagation();
      this.#removeKit(removeButton);
      return;
    }

    const stepButton = target.closest('[data-kit-qty-step]');
    if (!stepButton || !this.contains(stepButton)) return;
    event.preventDefault();
    event.stopPropagation();

    const step = Number(stepButton.getAttribute('data-kit-qty-step')) || 0;
    const next = this.#currentCount() + step;
    if (next < 1) {
      this.#applyCount(0);
      return;
    }

    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-kit-qty-input]'));
    if (input) input.value = String(next);
    this.#applyCount(next);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.matches('[data-kit-qty-input]')) return;
    const input = /** @type {HTMLInputElement} */ (target);
    const next = Math.max(1, Math.floor(Number(input.value) || 1));
    input.value = String(next);
    this.#applyCount(next);
  };

  /**
   * Build a {key: 0} map for every line item in this kit using DOM data-key
   * attributes — avoids a /cart.js fetch and the 429 rate-limit that comes
   * with it when remove is triggered shortly after an add.
   * @returns {Record<string, number>}
   */
  #buildRemoveUpdatesFromDOM() {
    const kitId = this.#kitId;
    /** @type {Record<string, number>} */
    const updates = {};
    const seen = new Set();
    // Both the kit-parent row and the kit-child rows carry data-kit-id +
    // data-key, so one querySelectorAll covers all line items in the kit.
    for (const el of document.querySelectorAll(`[data-kit-id="${CSS.escape(kitId)}"][data-key]`)) {
      const key = el instanceof HTMLElement ? el.dataset.key : null;
      if (key && !seen.has(key)) {
        seen.add(key);
        updates[key] = 0;
      }
    }
    return updates;
  }

  /**
   * Set the kit count to `nextCount`. A count of 0 removes the whole kit.
   * Updates every line sharing the kit id in a single /cart/update.js call.
   * @param {number} nextCount
   */
  async #applyCount(nextCount) {
    const kitId = this.#kitId;
    if (!kitId) return;

    // Optimistically advance the host's count so a fast second click reads this
    // new value (not the pre-click count, which would silently drop the step).
    // The morph re-renders the row with the server's truth right after.
    if (nextCount >= 1) this.dataset.kitCount = String(nextCount);

    if (this.#inFlight) this.#inFlight.abort();
    const controller = new AbortController();
    this.#inFlight = controller;

    return this.#runLocked(async () => {
      if (controller.signal.aborted) return;

      // For removal, read line keys directly from the DOM — no /cart.js fetch
      // needed, which prevents Shopify 429 rate-limit errors when remove fires
      // immediately after an add (both would fetch /cart.js in quick succession).
      if (nextCount < 1) {
        const updates = this.#buildRemoveUpdatesFromDOM();
        if (Object.keys(updates).length > 0) {
          try {
            await this.#commitUpdates(updates, controller.signal);
          } catch (err) {
            if (err && err.name === 'AbortError') return;
            console.error('[cart-kit] removeViaDOM', err);
          }
          return;
        }
        // DOM had no matching rows (stale state) — fall through to /cart.js path.
      }

      // The kit count the server actually holds right now, derived from the
      // pre-update cart. Used to roll the optimistic count back if the update
      // never lands. Derived in-task (not captured before the lock) so it is
      // correct even when a faster click coalesced ahead of this one.
      let serverCount = null;
      try {
        const cart = await this.#fetchCart(controller.signal);

        /** @type {Record<string, number>} */
        const updates = {};
        for (const line of cart.items) {
          const props = line.properties || {};
          if (props._kit_id !== kitId) continue;
          const unitRaw = Number(props._kit_unit_qty);
          const unit = Number.isFinite(unitRaw) && unitRaw > 0 ? unitRaw : 1;
          if (serverCount === null) serverCount = Math.max(1, Math.round(line.quantity / unit));
          updates[line.key] = nextCount < 1 ? 0 : nextCount * unit;
        }
        if (Object.keys(updates).length === 0) return;

        await this.#commitUpdates(updates, controller.signal);
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        console.error('[cart-kit] applyCount', err);
        // The optimistic count never landed (no morph re-rendered the row).
        // Restore the stepper + input to the server's last-known value so the
        // UI doesn't show an update that didn't happen.
        if (serverCount !== null) this.#setCount(serverCount);
      }
    });
  }

  /**
   * Sync the host's count attribute and the qty input to a value, without
   * issuing any cart mutation. Used to roll back a failed optimistic update.
   * @param {number} count
   */
  #setCount(count) {
    this.dataset.kitCount = String(count);
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-kit-qty-input]'));
    if (input) input.value = String(count);
  }

  /**
   * Remove the whole kit. Lives on the parent row's trash button; in the drawer
   * that button and the minus-below-1 path are the only remove affordances.
   * @param {Element} button
   */
  async #removeKit(button) {
    const kitId = button.getAttribute('data-kit-remove') || this.#kitId;
    if (!kitId) return;

    button.setAttribute('disabled', '');

    await this.#runLocked(async () => {
      try {
        const cart = await this.#fetchCart();

        /** @type {Record<string, number>} */
        const updates = {};
        for (const line of cart.items) {
          if (line.properties && line.properties._kit_id === kitId) {
            updates[line.key] = 0;
          }
        }
        // Nothing matched (kit already gone / cart drifted). Don't leave the
        // button permanently disabled with no feedback.
        if (Object.keys(updates).length === 0) return;

        await this.#commitUpdates(updates);
      } catch (err) {
        console.error('[cart-kit] removeKit', err);
      } finally {
        // Harmless if the row was morphed away on success; required so a
        // no-op/failed remove can be retried.
        button.removeAttribute('disabled');
      }
    });
  }

  /**
   * @param {AbortSignal} [signal]
   * @returns {Promise<{ items: Array<{ key: string, properties?: Record<string, string> }> }>}
   */
  async #fetchCart(signal) {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, signal });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  /**
   * POST the line updates, asking the server to re-render every cart section,
   * then broadcast the new cart state so the cart-items-component morphs.
   * Retries once after 600 ms on 429 (Shopify rate-limit) so rapid-click
   * sequences still land without requiring a page reload.
   * @param {Record<string, number>} updates
   * @param {AbortSignal} [signal]
   * @param {number} [attempt]
   */
  async #commitUpdates(updates, signal, attempt = 0) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: collectCartSectionIds(),
        sections_url: window.location.pathname,
      }),
      signal,
    });

    if (res.status === 429 && attempt < 3) {
      // Exponential backoff: 1 s, 2 s, 4 s. The rate-limit window on
      // Shopify dev stores is typically 2-5 s; production limits are far
      // more permissive, so this rarely triggers in real usage.
      await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      return this.#commitUpdates(updates, signal, attempt + 1);
    }

    if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);
    const data = await res.json();

    document.dispatchEvent(
      new CartUpdateEvent(data, 'cart-kit', {
        source: 'cart-kit',
        itemCount: data.item_count || 0,
        sections: data.sections,
      })
    );
  }
}

if (!customElements.get('cart-kit')) {
  customElements.define('cart-kit', CartKit);
}
